import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  TextField,
  Typography,
  Autocomplete,
  Snackbar,
  Alert,
  useTheme,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import HeaderBreadcrumbs from 'components/HeaderBreadcrumbs';
import { axios } from 'utils/axios';
import { GridSearchIcon } from '@mui/x-data-grid';
import { DatePicker } from '@mui/lab';
import { format, subDays, parse } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import CommonTable from 'components/common/CommonTable';
import CustomPaper from 'components/common/CustomPaper';
import { Container } from '@mui/material';
import useSettings from 'hooks/useSettings';
import { EmojiTransportation, InsertDriveFile, PictureAsPdf } from '@mui/icons-material';
import { Checkbox } from '@mui/material';

const defaultFilters = {
  search: '',
  pageSize: 10,
  client: '',
  weight1DateTimeFrom: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
  weight1DateTimeTo: format(new Date(), 'yyyy-MM-dd')
};

// Key for localStorage
const FILTERS_STORAGE_KEY = 'transactionsFilters';

const Transactions = () => {
  const navigate = useNavigate();
  const { themeStretch } = useSettings();
  const theme = useTheme();
  const [transactions, setTransactions] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState(() => {
    // Try to load filters from localStorage
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      try {
        return { ...defaultFilters, ...JSON.parse(saved) };
      } catch {
        return defaultFilters;
      }
    }
    return defaultFilters;
  });
  const [chipFilter, setChipFilters] = useState<any>({});
  const [client, setClient] = useState<any>([]);
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  // Copy Ticket feature state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [nextTicketNumber, setNextTicketNumber] = useState<string>('');
  const [nextTransNumber, setNextTransNumber] = useState<number | null>(null);
  const [copyingTicketId, setCopyingTicketId] = useState<number | null>(null);
  // Delete Ticket feature state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTicketId, setDeletingTicketId] = useState<number | null>(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const columns = [
    {
      field: 'Id',
      headerName: '',
      width: 100,
      renderHeader: () => (
        <Checkbox
          checked={selectAll}
          indeterminate={selectedIds.length > 0 && selectedIds.length < transactions.length}
          onChange={handleSelectAll}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      renderCell: (params: any) => {
        return (
          <Checkbox
            checked={params ? selectedIds.includes(params) : false}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              handleCheckboxChange(e, params);
            }}
          />
        );
      }
    },
    { field: 'TicketNumber', headerName: 'Ticket Number', flex: 1, minWidth: 150 },
    { field: 'VehicleReg', headerName: 'Vehicle', flex: 2, minWidth: 150 },
    { field: 'OriginatorName', headerName: 'Originator', flex: 2, minWidth: 250 },
    { field: 'Weight1DateTime', headerName: 'Reading 1 At', flex: 2, minWidth: 200 },
    { field: 'Weight1Value', headerName: 'First Weight', flex: 2, minWidth: 150 },
    { field: 'Weight2DateTime', headerName: 'Reading 2 At', flex: 2, minWidth: 200 },
    { field: 'Weight2Value', headerName: 'Second Weight', flex: 2, minWidth: 150 },
    { field: 'WeightNet', headerName: 'Net Weight', flex: 2, minWidth: 150 },
    { field: 'ServiceDescription', headerName: 'Service Description', flex: 2, minWidth: 250 },
    { field: 'CurrentStatus', headerName: 'Status', flex: 2, minWidth: 150 },
    { field: 'Client', headerName: 'Client', flex: 2, minWidth: 150 },
    { field: 'Haulier', headerName: 'Haulier', flex: 2, minWidth: 150 },
    { field: 'OriginatorAccRef', headerName: 'Originator Acc', flex: 2, minWidth: 150 },
    { field: 'DriverName', headerName: 'Driver', flex: 2, minWidth: 150 },
    { field: 'BridgeIdentity', headerName: 'Bridge Ref', flex: 2, minWidth: 150 },
    { field: 'Destination', headerName: 'Destination', flex: 2, minWidth: 150 },
    { field: 'OriginCouncil', headerName: 'Origin Council', flex: 2, minWidth: 150 }
  ];

  const fetchContacts = async (): Promise<void> => {
    try {
      const response = await axios.get('/rest/api/contacts/dropdown?isClient=1');
      const allContacts = response.data.data || [];
      setClient(
        allContacts.map((el: any) => ({ value: el.Name, id: el.Id, accountRef: el.AccountRef }))
      );
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  async function fetchTransactions(isClear?: boolean, searchFilters?: any, pageNumber?: number) {
    setLoading(true);

    try {
      const appliedFilters: any = isClear
        ? defaultFilters
        : searchFilters
        ? searchFilters
        : filters;

      // If search query is present, exclude date filters
      const filtersToUse = { ...appliedFilters };
      if (filtersToUse.search && filtersToUse.search.trim() !== '') {
        delete filtersToUse.weight1DateTimeFrom;
        delete filtersToUse.weight1DateTimeTo;
      }

      // Use provided page number or current page state
      const pageToUse = pageNumber !== undefined ? pageNumber : page;

      const query = new URLSearchParams({
        page: pageToUse.toString(),
        ...Object.entries(filtersToUse)
          .filter(([_, value]) => value !== '' && value !== null && value !== undefined)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
      }).toString();

      const response = await axios.get(`/rest/api/transactions?${query}`, {
        withCredentials: true
      });

      let data = response.data.data || [];

      if (data.length) {
        data = data.map((item: any) => ({
          ...item,
          Weight1DateTime: formatDateTime(item.Weight1DateTime),
          Weight2DateTime: formatDateTime(item.Weight2DateTime)
        }));
      }

      setTransactions(data);
      setTotalRows(response.data.pageMeta.totalRecords || 0);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Formats a date string to 'DD/MM/YYYY HH:mm:ss'
   */
  function formatDateTime(dateString: string): string {
    if (!dateString) return 'N/A';

    const [datePart, timePart] = dateString.split('T');
    if (!datePart || !timePart) return 'Invalid Date';

    const [year, month, day] = datePart.split('-');
    const [hour, minute, second] = timePart.split(':');

    return `${day}/${month}/${year} ${hour}:${minute}:${second?.slice(0, 2) || '00'}`;
  }

  function formatDateRange(startDate: string, endDate: string): string {
    if (!startDate || !endDate) return '';

    const start = new Date(startDate);
    const end = new Date(endDate);

    const sameMonth =
      start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
    const sameYear = start.getFullYear() === end.getFullYear();

    if (sameMonth) {
      return `${format(start, 'dd')} - ${format(end, 'dd MMM yyyy')}`;
    } else if (sameYear) {
      return `${format(start, 'dd MMM')} - ${format(end, 'dd MMM yyyy')}`;
    } else {
      return `${format(start, 'dd MMM yyyy')} - ${format(end, 'dd MMM yyyy')}`;
    }
  }

  useEffect(() => {
    fetchTransactions();
  }, [page, filters.pageSize, filters.client]);

  useEffect(() => {
    if (filters.weight1DateTimeFrom && filters.weight1DateTimeTo) {
      setChipFilters((prevFilters: any) => ({
        ...prevFilters,
        Date: formatDateRange(filters.weight1DateTimeFrom!, filters.weight1DateTimeTo!)
      }));
      fetchTransactions();
    } else {
      setChipFilters((prevFilters: any) => {
        const updatedChipFilters = { ...prevFilters };
        delete updatedChipFilters.Date;
        return updatedChipFilters;
      });
    }
  }, [filters.weight1DateTimeFrom, filters.weight1DateTimeTo]);

  const handleTicketSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = event.target.value;
    const updatedFilters = { ...filters, search: searchValue };
    setFilters(updatedFilters);
    setPage(1); // Reset to page 1 when search changes

    // Debounce API call
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    debounceTimeout.current = setTimeout(() => {
      fetchTransactions(false, updatedFilters, 1); // Pass page 1 explicitly
    }, 500); // Adjust debounce time as needed (500ms in this case)
  };

  function handleDateChange(key: string, value: any): void {
    const formatted = value ? format(value, 'yyyy-MM-dd') : null;
    setPage(1); // Reset to page 1 when date filter changes

    setFilters((prevFilters: any) => {
      const updatedFilters = { ...prevFilters, [key]: formatted };

      if (updatedFilters.weight1DateTimeFrom && updatedFilters.weight1DateTimeTo) {
        setChipFilters((prevFilters: any) => ({
          ...prevFilters,
          Date: formatDateRange(
            updatedFilters.weight1DateTimeFrom,
            updatedFilters.weight1DateTimeTo
          )
        }));
      } else {
        setChipFilters((prevFilters: any) => {
          const updatedChipFilters = { ...prevFilters };
          delete updatedChipFilters.Date;
          return updatedChipFilters;
        });
      }

      return updatedFilters;
    });
  }

  function handleRemoveFilter(key: string) {
    setPage(1); // Reset to page 1 when filter is removed
    setChipFilters((prevFilters: any) => {
      const updatedFilters = { ...prevFilters };
      delete updatedFilters[key];
      return updatedFilters;
    });

    if (key === 'Date') {
      setFilters((prevFilters: any) => ({
        ...prevFilters,
        weight1DateTimeFrom: '',
        weight1DateTimeTo: ''
      }));
    }
  }

  const clearFilters = () => {
    setPage(1); // Reset to page 1 when filters are cleared
    setFilters(defaultFilters);
    setChipFilters({});
    fetchTransactions(true);
  };
  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, id: number) => {
    if (event.target.checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = transactions.map((transaction: any) => transaction.Id);
      setSelectedIds(allIds);
      setSelectAll(true);
    } else {
      setSelectedIds([]);
      setSelectAll(false);
    }
  };

  const handleGenerateReport = async (type: 'pdf' | 'excel', selectedIds?: number[]) => {
    try {
      let queryParams = new URLSearchParams();

      if (selectedIds && selectedIds.length > 0) {
        queryParams.append('transactionIds', selectedIds.join(','));
      } else {
        queryParams.append('weight1DateTimeFrom', filters.weight1DateTimeFrom);
        queryParams.append('weight1DateTimeTo', filters.weight1DateTimeTo);
      }

      // if (filters.clientId) {
      //   queryParams.append('clientId', filters.clientId);
      // }

      const endpoint =
        type === 'pdf'
          ? '/rest/api/transactions/generate-pdf'
          : '/rest/api/transactions/generate-excel';

      const response = await axios.post(
        `${endpoint}?${queryParams.toString()}`,
        {},
        {
          withCredentials: true
        }
      );

      setSnackbarMessage(
        `Report generated and sent to your email. You can also check the report section for later use.`
      );
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error generating report:', error);
      setSnackbarMessage('Failed to generate report. Please try again.');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleRowClick = (params: any) => {
    navigate(`/dashboard/transactions/edit/${params.row.Id}`);
  };
  const handleEdit = (params: any) => {
    navigate(`/dashboard/transactions/edit/${params}`);
  };
  const handleCreateTransaction = () => {
    navigate('/dashboard/transactions/new');
  };
  const handleDownloadPDF = async (id: number) => {
    try {
      const response = await axios.get(`/rest/api/transactions/${id}/download-pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transaction_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setSnackbarMessage('Failed to download PDF. Please try again.');
      setSnackbarOpen(true);
    }
  };

  // Copy Ticket Feature
  const handleCopyTicket = async (id: number) => {
    try {
      // Get next ticket number
      const response = await axios.get('/rest/api/transactions/next-ticket-number');
      // API returns { nextTransNumber, ticketNumber }
      const ticketNumber = response.data?.data?.ticketNumber || '';
      const transNumber = response.data?.data?.nextTransNumber || null;
      setNextTicketNumber(ticketNumber);
      setNextTransNumber(transNumber);
      setCopyingTicketId(id);
      setCopyDialogOpen(true);
    } catch (error) {
      setSnackbarMessage('Failed to fetch next ticket number.');
      setSnackbarOpen(true);
    }
  };

  const confirmCopyTicket = async () => {
    if (!copyingTicketId) return;
    try {
      await axios.post(`/rest/api/transactions/copy-ticket/${copyingTicketId}`);
      setSnackbarMessage(`Ticket copied! New Ticket Number: ${nextTicketNumber}`);
      setSnackbarOpen(true);
      setCopyDialogOpen(false);
      setCopyingTicketId(null);
      setNextTicketNumber('');
      setNextTransNumber(null);
      fetchTransactions();
    } catch (error) {
      setSnackbarMessage('Failed to copy ticket.');
      setSnackbarOpen(true);
      setCopyDialogOpen(false);
    }
  };

  const cancelCopyTicket = () => {
    setCopyDialogOpen(false);
    setCopyingTicketId(null);
    setNextTicketNumber('');
    setNextTransNumber(null);
  };

  // Delete Ticket Feature
  const handleDeleteTicket = async (id: number) => {
    setDeletingTicketId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteTicket = async () => {
    if (!deletingTicketId) return;
    setDeleteLoading(true);
    try {
      await axios.delete(`/rest/api/transactions/${deletingTicketId}`);
      setSnackbarMessage('Ticket deleted successfully!');
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setDeletingTicketId(null);
      fetchTransactions();
    } catch (error) {
      console.error('Error deleting ticket:', error);
      setSnackbarMessage('Failed to delete ticket. Please try again.');
      setSnackbarOpen(true);
      setDeleteDialogOpen(false);
      setDeletingTicketId(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelDeleteTicket = () => {
    setDeleteDialogOpen(false);
    setDeletingTicketId(null);
  };

  // Bulk Delete Feature
  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setDeleteLoading(true);
    try {
      await axios.post('/rest/api/transactions/delete-multiple', {
        transactionIds: selectedIds.map((id) => id.toString())
      });
      setSnackbarMessage(`${selectedIds.length} ticket(s) deleted successfully!`);
      setSnackbarOpen(true);
      setBulkDeleteDialogOpen(false);
      setSelectedIds([]);
      setSelectAll(false);
      fetchTransactions();
    } catch (error) {
      console.error('Error deleting tickets:', error);
      setSnackbarMessage('Failed to delete tickets. Please try again.');
      setSnackbarOpen(true);
      setBulkDeleteDialogOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  const cancelBulkDelete = () => {
    setBulkDeleteDialogOpen(false);
  };
  const customActions = (row: any) => (
    <>
      <Button
        variant="outlined"
        color="primary"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          handleEdit(row.Id);
        }}
      >
        Edit
      </Button>
      <Button
        variant="outlined"
        color="secondary"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          handleDownloadPDF(row.Id);
        }}
      >
        Download PDF
      </Button>
      <Button
        variant="outlined"
        color="success"
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          handleCopyTicket(row.Id);
        }}
      >
        Copy Ticket
      </Button>
      <Button
        variant="outlined"
        color="error"
        size="small"
        disabled={deleteLoading}
        onClick={(event) => {
          event.stopPropagation();
          handleDeleteTicket(row.Id);
        }}
      >
        {deleteLoading && deletingTicketId === row.Id ? (
          <CircularProgress size={16} color="inherit" />
        ) : (
          'Delete'
        )}
      </Button>
    </>
  );

  // Persist filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  // Reset select all when transactions change
  useEffect(() => {
    if (transactions.length === 0) {
      setSelectAll(false);
    } else {
      setSelectAll(selectedIds.length === transactions.length);
    }
  }, [transactions, selectedIds]);

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  return (
    <Container maxWidth={themeStretch ? false : 'lg'}>
      <Box sx={{ padding: 3, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 3
          }}
        >
          <HeaderBreadcrumbs
            heading="Ticket Transactions"
            links={[
              { name: 'Dashboard', href: '/dashboard' },
              { name: 'Ticket Transactions', href: '/dashboard/transactions' }
            ]}
          />

          <Button
            variant="contained"
            onClick={handleCreateTransaction}
            startIcon={<InsertDriveFile />}
          >
            Create Transaction
          </Button>
        </Box>

        <CustomPaper>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', marginBottom: 2 }}>
            <DatePicker
              label="Start Date"
              inputFormat="dd/MM/yyyy"
              value={
                filters.weight1DateTimeFrom
                  ? parse(filters.weight1DateTimeFrom, 'yyyy-MM-dd', new Date())
                  : null
              }
              onChange={(newValue) => handleDateChange('weight1DateTimeFrom', newValue)}
              renderInput={(params) => <TextField {...params} variant="outlined" size="medium" />}
            />
            <DatePicker
              label="End Date"
              inputFormat="dd/MM/yyyy"
              value={
                filters.weight1DateTimeTo
                  ? parse(filters.weight1DateTimeTo, 'yyyy-MM-dd', new Date())
                  : null
              }
              onChange={(newValue) => handleDateChange('weight1DateTimeTo', newValue)}
              renderInput={(params) => <TextField {...params} variant="outlined" size="medium" />}
            />
            <Autocomplete
              options={client}
              getOptionLabel={(option: { value: string; id: string }) => option.value}
              onChange={(_, newValue) => {
                setPage(1); // Reset to page 1 when client filter changes
                setFilters((prev: any) => ({
                  ...prev,
                  client: newValue ? newValue.value : ''
                }));
              }}
              renderInput={(params) => <TextField {...params} label="Select Client" />}
              sx={{ width: 300 }}
            />
            <TextField
              variant="outlined"
              placeholder="Search by Ticket, VehicleReg, Driver, Source, Destination"
              size="medium"
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <GridSearchIcon />
                  </InputAdornment>
                )
              }}
              value={filters.search}
              onChange={handleTicketSearch}
            />
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={() => handleGenerateReport('pdf', selectedIds)}
              disabled={transactions.length === 0}
            >
              Generate PDF Report
            </Button>

            <Button
              variant="contained"
              startIcon={<InsertDriveFile />}
              onClick={() => handleGenerateReport('excel')}
              disabled={transactions.length === 0 || (selectedIds && selectedIds.length > 0)} // Disable if selectedIds has values
            >
              Generate Excel Report
            </Button>
          </Box>

          {/* Bulk Actions Bar */}
          {selectedIds.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                padding: 2,
                border: `1px solid ${theme.palette.primary.main}`,
                borderRadius: 1,
                marginBottom: 2,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(25, 118, 210, 0.1)'
                    : 'rgba(25, 118, 210, 0.05)'
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {selectedIds.length} ticket(s) selected
              </Typography>
              <Button
                variant="contained"
                color="error"
                disabled={deleteLoading}
                onClick={handleBulkDelete}
                sx={{ ml: 'auto' }}
              >
                {deleteLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  `Delete Selected (${selectedIds.length})`
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedIds([]);
                  setSelectAll(false);
                }}
              >
                Clear Selection
              </Button>
            </Box>
          )}
          <Box>
            <Typography fontWeight={900}>{totalRows} results found</Typography>
          </Box>
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '8px',
                borderRadius: '8px'
              }}
            >
              {Object.entries(chipFilter)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value], ind) => (
                  <Box
                    key={ind}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      border: '3px dotted #333d47',
                      padding: '8px',
                      borderRadius: '8px'
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {key}:
                    </Typography>
                    <Chip label={String(value)} onDelete={() => handleRemoveFilter(key)} />
                  </Box>
                ))}
              {Object.keys(chipFilter).length > 0 && (
                <Button
                  startIcon={<DeleteIcon />}
                  color="error"
                  size="large"
                  onClick={clearFilters}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Box>
          <CommonTable
            rows={transactions}
            columns={columns}
            pageSize={filters.pageSize}
            totalRecords={totalRows}
            loading={loading}
            currentPage={page - 1}
            handlePageChange={(newPage) => setPage(newPage + 1)}
            handlePageSizeChange={(newPageSize) => {
              setFilters((prevFilters: any) => ({ ...prevFilters, pageSize: newPageSize }));
            }}
            onRowClick={handleRowClick}
            onEdit={handleEdit}
            customActions={customActions}
            // noAction={true}
          />
        </CustomPaper>
      </Box>
      {/* Copy Ticket Confirmation Dialog */}
      {copyDialogOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              background: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
              padding: 4,
              borderRadius: 2,
              boxShadow: 3,
              minWidth: 350
            }}
          >
            <Typography variant="h6" gutterBottom>
              Copy Ticket Confirmation
            </Typography>
            <Typography gutterBottom>
              Next available ticket number: <b>{nextTicketNumber}</b>
              <br />
              Next transaction number: <b>{nextTransNumber}</b>
              <br />
              Do you want to copy this ticket?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button variant="contained" color="success" onClick={confirmCopyTicket}>
                Yes, Copy
              </Button>
              <Button variant="outlined" color="error" onClick={cancelCopyTicket}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {/* Delete Ticket Confirmation Dialog */}
      {deleteDialogOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              background: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
              padding: 4,
              borderRadius: 2,
              boxShadow: 3,
              minWidth: 350
            }}
          >
            <Typography variant="h6" gutterBottom>
              Delete Ticket Confirmation
            </Typography>
            <Typography gutterBottom>
              Are you sure you want to delete this ticket transaction? This action cannot be undone.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="error"
                disabled={deleteLoading}
                onClick={confirmDeleteTicket}
              >
                {deleteLoading ? <CircularProgress size={16} color="inherit" /> : 'Yes, Delete'}
              </Button>
              <Button variant="outlined" disabled={deleteLoading} onClick={cancelDeleteTicket}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {/* Bulk Delete Confirmation Dialog */}
      {bulkDeleteDialogOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.7)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Box
            sx={{
              background: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
              padding: 4,
              borderRadius: 2,
              boxShadow: 3,
              minWidth: 400
            }}
          >
            <Typography variant="h6" gutterBottom>
              Delete Multiple Tickets Confirmation
            </Typography>
            <Typography gutterBottom>
              Are you sure you want to delete <strong>{selectedIds.length}</strong> selected
              ticket(s)? This action cannot be undone.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="error"
                disabled={deleteLoading}
                onClick={confirmBulkDelete}
              >
                {deleteLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  `Yes, Delete All (${selectedIds.length})`
                )}
              </Button>
              <Button variant="outlined" disabled={deleteLoading} onClick={cancelBulkDelete}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Transactions;
